import { SeoHelmet, NoIndexHelmet } from "@/app/_seo/SeoHelmet";
import {
  ORGANIZATION_ID,
  WEBSITE_ID,
  breadcrumbSchema,
  jsonLdGraph,
  organizationSchema,
  serviceSchema,
  websiteSchema,
} from "@/app/_seo/schema";
import { DEFAULT_OG_IMAGE_URL, NAVIGATION_URLS, absolutePublicUrl } from "@/app/_utils/constants";
import { PRIVATE_EVENTS_SEO_DESCRIPTION } from "@/app/(public)/private-events/privateEventsSeo";
import {
  CURRENT_NEWSLETTER,
  newsletterHref,
  type NewsletterIssue,
} from "@/app/(public)/newsletter/newsletterContent";

const faqVisibleItems = [
  {
    question: "How does the waitlist work?",
    answer:
      "First, fill out the waitlist form. We go through it chronologically and when we reach your spot, we will send you the info about any upcoming shows.",
  },
  {
    question: "If I've already been to a show, do I need to re-signup for the waitlist?",
    answer: "Yes, but always feel free to DM us if you're unsure.",
  },
  {
    question: "What if i want a refund?",
    answer: "You can get a refund up to 72 hours before showtime.",
  },
  {
    question: "Can I transfer my ticket?",
    answer: "Yes, but tell your friend to use your name at the door.",
  },
  {
    question: "Do you ever repeat venues?",
    answer:
      "Yes, we love going back to our favorite venues. Some, like Astor Place Hairstylists, we do multiple times a year. Others, like Katz's Deli and Economy Candy, are annual events!",
  },
  {
    question: "What is the name of our business?",
    answer:
      "It depends who's asking. Underground Overground Comedy, Underground Overground, LLC, Comedy UO, Comedy overground underground, Comedyuo, Comedy.uo, UOcomedy",
  },
  {
    question: "How are you different than a regular comedy club?",
    answer:
      "ComedyUO is a pop-up comedy show, appearing in strange places like laundromats, barbershops, restaurants, and more. Our tickets are all inclusive, so if you're looking for a top tier comedy show near you, look us up and grab a ticket. We bring you an end-to-end experience without the stress of racking up the bill.",
  },
  {
    question: "Who do you feature in your lineups?",
    answer:
      "We bring in top-tier talent you'd recognize from Netflix, SNL, and late-night TV. Our lineups stay under wraps because our audience trusts us, and half the fun is the surprise!",
  },
  {
    question: "Do you offer private or corporate comedy events?",
    answer:
      "Yes. We produce private comedy and magic events for companies, brands, and organizations across NYC. We are able to produce events in any of the 90+ venues in our rolodex, allowing us to produce unforgettable private events for teams, clients, and partners. From law firms and financial institutions to startups and tech companies, we deliver bespoke, turnkey live entertainment with the same level of talent, production, and care as our public shows. Our shows are completely customizable, from F&B, to talent, to the venue, we will cater to your needs.",
  },
  {
    question: "Do you host branded or sponsored comedy shows?",
    answer:
      "We work with brands to create custom comedy experiences, sponsor our tentpole shows, and produce branded content through comedy tapings. Sponsorship opportunities include on-site branding, social media distribution, email promotion, and content that lives beyond the event.",
  },
  {
    question: "Are we a good date night spot?",
    answer:
      "If you're on a 1st date, enter at your own risk. Couples and double dates come to shows all the time, but if you're looking to bring that crush from the office you're trying to impress for the first time, be ready for some crowdwork!",
  },
  {
    question: "What are the best unique comedy shows in NYC?",
    answer:
      "ComedyUO produces pop-up comedy in over 90 unique New York City venues, from Katz's Delicatessen and Economy Candy to laundromats, distilleries, and retail stores. Lineups stay secret until showtime, tickets are all-inclusive, and every show is built around the room. Browse upcoming public shows at comedyuo.com/tickets or join the waitlist for first access.",
  },
  {
    question: "Where can I see comedy in iconic New York locations?",
    answer:
      "ComedyUO has produced comedy shows at Katz's Delicatessen, the Empire State Building, Peter Luger's, Paragon Sports, Economy Candy, and dozens of other iconic NYC businesses. We turn the city's most recognizable spaces into one-night comedy rooms. See what's coming up at comedyuo.com/tickets.",
  },
  {
    question: "What is a good alternative to traditional comedy clubs in NYC?",
    answer:
      "ComedyUO is a pop-up comedy company, not a brick-and-mortar club. Shows happen in delis, stores, restaurants, and landmarks across the city. Tickets are all-inclusive with no drink minimum, and lineups are kept secret until the comics hit the stage. It is a different kind of night out than a basement club on a standard two-drink-minimum bill.",
  },
  {
    question: "Do you provide private or corporate magicians in NYC?",
    answer:
      "Yes. ComedyUO books close-up magicians, mentalists, and stage performers for corporate dinners, client events, member clubs, birthdays, and weddings across New York City. Private magic can be a walk-around set or a full seated show. Learn more at comedyuo.com/magic or comedyuo.com/private-events.",
  },
];

function crumbs(...items: { name: string; path: string }[]) {
  return breadcrumbSchema([{ name: "Home", path: "/" }, ...items]);
}

export function AboutLayoutHelmet() {
  return (
    <SeoHelmet
      title="About Us | Underground Overground Comedy"
      description="David and Ethan founded ComedyUO in April 2021. Underground Overground Comedy produces pop-up comedy shows in unique NYC venues, with all-inclusive tickets and secret lineups."
      canonicalPath="/about"
      ogImage={absolutePublicUrl("/images/Katz_s_Storefront.jpg")}
      ogImageAlt="Katz's Delicatessen storefront, a ComedyUO venue"
      jsonLd={jsonLdGraph([
        organizationSchema(),
        crumbs({ name: "About", path: "/about" }),
        {
          "@type": "AboutPage",
          name: "About Us",
          url: NAVIGATION_URLS.ABOUT,
          isPartOf: { "@id": "https://comedyuo.com/#website" },
          about: { "@id": "https://comedyuo.com/#organization" },
        },
      ])}
    />
  );
}

export function PrivateEventsLayoutHelmet() {
  const description = PRIVATE_EVENTS_SEO_DESCRIPTION;
  return (
    <SeoHelmet
      title="Private Comedy & Magic Events NYC | Corporate Entertainment | ComedyUO"
      description={description}
      canonicalPath="/private-events"
      ogImage={absolutePublicUrl("/images/Ethan_And_David_-_Willy_Wonka_2.webp")}
      ogImageAlt="Private comedy and magic events with ComedyUO in New York City"
      jsonLd={jsonLdGraph([
        organizationSchema(),
        websiteSchema(),
        crumbs({ name: "Private Events", path: "/private-events" }),
        {
          "@type": "WebPage",
          name: "Private Comedy and Magic Events in NYC",
          url: NAVIGATION_URLS.PRIVATE_EVENTS,
          inLanguage: "en-US",
          isPartOf: { "@id": WEBSITE_ID },
          about: { "@id": ORGANIZATION_ID },
          description,
          primaryImageOfPage: absolutePublicUrl("/images/Ethan_And_David_-_Willy_Wonka_2.webp"),
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: ["h1", "h2"],
          },
        },
        serviceSchema({
          name: "Private comedy and magic events",
          serviceType: "Corporate and private live entertainment",
          description,
          url: NAVIGATION_URLS.PRIVATE_EVENTS,
        }),
      ])}
    />
  );
}

/** @deprecated Use PrivateEventsLayoutHelmet — /contact redirects to /private-events */
export function ContactLayoutHelmet() {
  return <PrivateEventsLayoutHelmet />;
}

export function FaqLayoutHelmet() {
  return (
    <SeoHelmet
      title="FAQ | Underground Overground Comedy"
      description="Answers about the ComedyUO waitlist, refunds, ticket transfers, venues, private events, and how pop-up comedy shows work in New York City."
      canonicalPath="/faq"
      ogImage={absolutePublicUrl("/images/Ethan_And_David_-_Willy_Wonka_2.webp")}
      ogImageAlt="ComedyUO frequently asked questions"
      jsonLd={jsonLdGraph([
        organizationSchema(),
        crumbs({ name: "FAQ", path: "/faq" }),
        {
          "@type": "FAQPage",
          url: NAVIGATION_URLS.FAQ,
          mainEntity: faqVisibleItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer },
          })),
        },
      ])}
    />
  );
}

export function HomeLayoutHelmet() {
  return (
    <SeoHelmet
      title="Underground Overground Comedy | Pop-Up Comedy Shows in NYC"
      description="Unique comedy shows and fun nights out in NYC. Pop-up comedy and magic in iconic New York locations, with secret lineups and all-inclusive tickets. An alternative to traditional comedy clubs."
      canonicalPath="/"
      ogImage={DEFAULT_OG_IMAGE_URL}
      ogImageAlt="Comedy show at Katz's Delicatessen, Underground Overground Comedy"
      jsonLd={jsonLdGraph([organizationSchema(), websiteSchema()])}
    />
  );
}

export function PressLayoutHelmet() {
  return (
    <SeoHelmet
      title="Press | Underground Overground Comedy"
      description="Press coverage of Underground Overground Comedy, including Deadline, New York Post, People, Time Out, The New Yorker, and Untapped NYC."
      canonicalPath="/press"
      ogImage={absolutePublicUrl("/images/press-1.webp")}
      ogImageAlt="ComedyUO press coverage"
      jsonLd={jsonLdGraph([
        organizationSchema(),
        crumbs({ name: "Press", path: "/press" }),
        {
          "@type": "CollectionPage",
          name: "Press",
          url: NAVIGATION_URLS.PRESS,
          about: { "@id": "https://comedyuo.com/#organization" },
        },
      ])}
    />
  );
}

export function NewsletterLayoutHelmet({
  issue = CURRENT_NEWSLETTER,
}: {
  issue?: NewsletterIssue;
}) {
  const isLatest = issue.slug === CURRENT_NEWSLETTER.slug;
  const path = newsletterHref(issue);
  const title = isLatest
    ? "The Underground Newsletter | Behind the Scenes at ComedyUO"
    : `${issue.edition} | The Underground Newsletter | ComedyUO`;
  const description = isLatest
    ? "A weekly behind-the-scenes newsletter from Underground Overground Comedy in NYC. Comics to watch, Magic UO updates, venue stories, and founder notes."
    : `${issue.edition}: ${issue.heroSummary}`;
  const ogImage = issue.comic.image || absolutePublicUrl("/comedyuo-logo-white.png");
  const pageUrl = isLatest ? NAVIGATION_URLS.NEWSLETTER : `https://comedyuo.com${path}`;
  const dateModified = issue.publishedAt;
  const blogUrl = NAVIGATION_URLS.NEWSLETTER;

  return (
    <SeoHelmet
      title={title}
      description={description}
      canonicalPath={path}
      ogType="article"
      ogImage={ogImage}
      ogImageAlt={`${issue.edition} of The Underground newsletter`}
      jsonLd={jsonLdGraph([
        organizationSchema(),
        isLatest
          ? crumbs({ name: "Newsletter", path: "/newsletter" })
          : crumbs(
              { name: "Newsletter", path: "/newsletter" },
              { name: issue.edition, path }
            ),
        {
          "@type": "Blog",
          "@id": `${blogUrl}#blog`,
          name: "The Underground",
          description:
            "A weekly behind-the-scenes newsletter from Underground Overground Comedy in NYC.",
          url: blogUrl,
          publisher: { "@id": "https://comedyuo.com/#organization" },
          inLanguage: "en-US",
        },
        {
          "@type": "BlogPosting",
          "@id": `${pageUrl}#post`,
          headline: `${issue.edition}: ${issue.heroTitle}`,
          description: issue.heroSummary,
          image: [issue.comic.image, issue.magic.image, issue.story.image].filter(Boolean),
          datePublished: dateModified,
          dateModified,
          mainEntityOfPage: pageUrl,
          isPartOf: { "@id": `${blogUrl}#blog` },
          author: {
            "@type": "Person",
            name: "David Levine",
            worksFor: { "@id": "https://comedyuo.com/#organization" },
          },
          publisher: { "@id": "https://comedyuo.com/#organization" },
          about: { "@id": "https://comedyuo.com/#organization" },
        },
      ])}
    />
  );
}

export function TicketsLayoutHelmet() {
  return (
    <SeoHelmet
      title="Tickets and Upcoming Shows | Underground Overground Comedy"
      description="Upcoming pop-up comedy shows in NYC and fun things to do for a night out. Unique comedy in iconic New York locations, with secret lineups and all-inclusive tickets."
      canonicalPath="/tickets"
      ogImage={absolutePublicUrl("/images/ticket-2.webp")}
      ogImageAlt="Upcoming ComedyUO shows"
      jsonLd={jsonLdGraph([
        organizationSchema(),
        crumbs({ name: "Tickets", path: "/tickets" }),
        {
          "@type": "CollectionPage",
          name: "Tickets and Upcoming Shows",
          url: NAVIGATION_URLS.TICKETS,
          about: { "@id": "https://comedyuo.com/#organization" },
        },
      ])}
    />
  );
}

export function WorkWithUsLayoutHelmet() {
  const description =
    "Partner with Underground Overground Comedy for brand partnerships, sponsorships, and activations around pop-up comedy shows in New York City.";
  return (
    <SeoHelmet
      title="Work With Us | Underground Overground Comedy"
      description={description}
      canonicalPath="/work-with-us"
      ogImage={absolutePublicUrl("/images/Ethan_And_David_-_Willy_Wonka_2.webp")}
      ogImageAlt="Work with Underground Overground Comedy"
      jsonLd={jsonLdGraph([
        organizationSchema(),
        crumbs({ name: "Work With Us", path: "/work-with-us" }),
        serviceSchema({
          name: "Brand partnerships and sponsorships",
          serviceType: "Brand partnership",
          description,
          url: NAVIGATION_URLS.WORK_WITH_US,
        }),
      ])}
    />
  );
}

export function WaitlistLayoutHelmet() {
  return (
    <SeoHelmet
      title="Join the Waitlist | Underground Overground Comedy"
      description="Join The Underground for first access to ComedyUO's biggest pop-up comedy shows in New York City before tickets are released to the public."
      canonicalPath="/waitlist"
      jsonLd={jsonLdGraph([
        organizationSchema(),
        crumbs({ name: "Waitlist", path: "/waitlist" }),
      ])}
    />
  );
}

export function MagicLayoutHelmet() {
  const description =
    "Book private and corporate magicians in NYC. Close-up magic, mentalism, and stage shows for client events, dinners, and branded activations.";
  return (
    <SeoHelmet
      title="Private and Corporate Magicians in NYC | ComedyUO Magic"
      description={description}
      canonicalPath="/magic"
      ogImage={DEFAULT_OG_IMAGE_URL}
      ogImageAlt="ComedyUO magic shows and private events in New York City"
      jsonLd={jsonLdGraph([
        organizationSchema(),
        crumbs({ name: "Magic", path: "/magic" }),
        serviceSchema({
          name: "Magic shows and private event magic",
          serviceType: "Live magic entertainment",
          description,
          url: NAVIGATION_URLS.MAGIC,
        }),
      ])}
    />
  );
}


export function PrivateEntertainmentLayoutHelmet() {
  return (
    <SeoHelmet
      title="Kings Athletic Club"
      description="Private comedy and magic for Kings Athletic Club."
      canonicalPath="/sc-holdings"
      ogImage="https://storage.googleapis.com/cuo-website/King's%20Athletic%20Club.png"
      ogImageAlt="Kings Athletic Club"
      indexable={false}
    />
  );
}

export function NotFoundHelmet() {
  return (
    <NoIndexHelmet
      title="Page not found | Underground Overground Comedy"
      description="This page does not exist or is no longer available."
      canonicalPath="/"
    />
  );
}

export function UtilityNoIndexHelmet({
  title,
  path,
}: {
  title: string;
  path: string;
}) {
  return (
    <NoIndexHelmet
      title={title}
      description="This page is not intended for search results."
      canonicalPath={path}
    />
  );
}
